/* eslint-disable jsx-a11y/alt-text */
import * as React from 'react'
import ChatbotWrapper from '../../../components/ChatbotWrapper'
import styles from './styles.module.scss'
import TwoColumn from '../../../components/TwoColumn'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTwitch } from '@fortawesome/free-brands-svg-icons'
import { faMusic, faHeart, faDownload, faClock, faShield, faSitemap } from '@fortawesome/free-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { ReactComponent as OBSLogo } from '../assets/obs.svg'
import { ReactComponent as VTSLogo } from '../assets/vts.svg'
import Section from '../../../components/Section'
import List from '../../../components/List'
import DownloadAndDiscordButtons from '../../../components/DownloadAndDiscordButtons'
import DiscordLink from '../../../components/DiscordLink'

type Props = {}

const Features: React.FC<Props> = () => {
  return (
    <ChatbotWrapper
      decorate
      containerWrap={false}
      title='Features'
      titleButtons={
        <div>
          <a href='#chat-events' title='Chat & Twitch Events' className={styles.iconButton}>
            <FontAwesomeIcon icon={faTwitch as IconDefinition} />
          </a>
          <a href='#obs' title='OBS Studio Control' className={styles.iconButton}>
            <OBSLogo />
          </a>
          <a href='#vts' title='VTube Studio Integration' className={styles.iconButton}>
            <VTSLogo />
          </a>
          <a href='#audio' title='Audio, Music & TTS' className={styles.iconButton}>
            <FontAwesomeIcon icon={faMusic as IconDefinition} />
          </a>
          <a href='#timers-counters' title='Timers & Counters' className={styles.iconButton}>
            <FontAwesomeIcon icon={faClock as IconDefinition} />
          </a>
          <a href='#advanced' title='Advanced Logic' className={styles.iconButton}>
            <FontAwesomeIcon icon={faSitemap as IconDefinition} />
          </a>
          <a href='#reliability' title='Reliability' className={styles.iconButton}>
            <FontAwesomeIcon icon={faShield as IconDefinition} />
          </a>
          <a href='#love' title='Community & Extras' className={styles.iconButton}>
            <FontAwesomeIcon icon={faHeart as IconDefinition} />
          </a>
        </div>
      }
      header={
        <>
          <p>Draco.Bot replaces multiple Twitch tools with one reliable desktop app that automates chat, OBS, audio, and stream events - without cloud lag or flaky connections.</p>
          <p>
            It runs locally on your machine and is designed to stay responsive and stable during real streams. If you ever want help, ideas, or updates, come hang out in our <DiscordLink />.
          </p>
          <DownloadAndDiscordButtons />
        </>
      }
    >
      <div className={styles.container}>
        <Section id='chat-events' title='Chat & Twitch Events' icon={<FontAwesomeIcon icon={faTwitch as IconDefinition} className={styles.icon} />}>
          <p>Automate how your stream reacts to viewers and live events - from simple shoutouts to multi-step actions triggered by channel points, raids, subs, and more.</p>

          <TwoColumn className={styles.subsection}>
            <List
              title='Respond to Events'
              items={[
                'Follows',
                'Subscriptions (all tiers)',
                'Gifted subs',
                'Raids',
                'Bits & Hype Trains',
                'Advertisements (start / end)',
                'Channel point redemptions',
                'Chat messages (including non-commands)',
              ]}
            />
            <List
              className={styles.second}
              title='Perform Actions'
              items={[
                'Send chat messages',
                'Create announcements (with colors)',
                'Create shoutouts',
                'Toggle emote-only / follower-only / subscriber-only',
                'Enable / disable commands automatically',
                'Mark channel point redemptions as fulfilled',
              ]}
            />
          </TwoColumn>

          <p className={styles.reasoning}>Your stream reacts instantly and consistently - even when you&apos;re busy.</p>
        </Section>

        <Section id='obs' title='OBS Studio Control' icon={<OBSLogo className={styles.icon} />}>
          <p>Control OBS directly from Draco.Bot - triggered by chat, channel points, timers, hotkeys, or stream events.</p>
          <p>
            Draco.Bot requires <b>OBS Studio v28+</b>.
          </p>

          <List
            title='OBS Actions'
            items={['Switch scenes', 'Show / hide sources', 'Toggle filters', 'Set source text', 'Set media source file', 'OBS audio control (mute, unmute, set volume)', 'React to OBS scene changes']}
          />

          <p className={styles.reasoning}>No alt-tabbing - OBS reacts automatically, exactly when it should.</p>
        </Section>

        <Section id='vts' title='VTube Studio Integration' icon={<VTSLogo className={styles.icon} />}>
          <p>Make your model part of the interaction. Trigger VTube Studio actions from chat, events, timers, or hotkeys.</p>

          <List
            title='VTube Studio Actions'
            items={[
              'Change models',
              'Move, rotate, and resize your model',
              'Trigger expressions / animations',
              'Execute hotkeys by name',
              'Wait for movements to finish before continuing other actions',
            ]}
          />

          <p className={styles.reasoning}>Your VTuber setup becomes a first-class part of your stream automation.</p>
        </Section>

        <Section id='audio' title='Audio, Music & Text-to-Speech' icon={<FontAwesomeIcon icon={faMusic as IconDefinition} className={styles.icon} />}>
          <p>Play music, sound effects, and TTS locally, routed exactly where you want them - with a built-in jukebox and playlist system.</p>

          <TwoColumn className={styles.subsection}>
            <List
              title='Music & Sound'
              items={['Built-in jukebox (drag & drop)', 'Multiple playlists with quick switching', 'Viewer music requests (optional)', 'Sound effects', 'Song output files for OBS overlays']}
            />
            <List
              className={styles.second}
              title='Text-to-Speech'
              items={['Microsoft & Amazon TTS providers', 'More robust SSML handling', 'Timeouts to prevent “stuck” queues', 'Choose audio devices per feature (songs / SFX / TTS)']}
            />
          </TwoColumn>

          <p className={styles.reasoning}>No stream delay, no browser audio issues - full control over where sound goes.</p>
        </Section>

        <Section id='timers-counters' title='Timers, Counters & Overlays' icon={<FontAwesomeIcon icon={faClock as IconDefinition} className={styles.icon} />}>
          <p>Build real stream mechanics: timers that run actions, subathon-style countdowns, counters for milestones, and overlays that update automatically.</p>

          <TwoColumn className={styles.subsection}>
            <List
              title='Timers'
              items={[
                'Timers and countdown timers',
                'Extendable countdowns (subs / bits)',
                'Start / stop / reset timers manually or via actions',
                'Run actions when timers start or complete',
                'Stream detail output for countdown timers',
              ]}
            />
            <List
              className={styles.second}
              title='Counters & Output Files'
              items={['Create and manage counters', 'Increment / decrement counters via actions', 'Write output files for OBS overlays', 'Use counters and timer details in overlay content']}
            />
          </TwoColumn>

          <p className={styles.reasoning}>Overlays and stream state stay accurate automatically - no manual tracking mid-stream.</p>
        </Section>

        <Section id='advanced' title='Advanced Logic When You Need It' icon={<FontAwesomeIcon icon={faSitemap as IconDefinition} className={styles.icon} />}>
          <p>Keep complex setups predictable. Draco.Bot includes queues and conditional actions so automations don&apos;t overlap or fight each other.</p>

          <TwoColumn className={styles.subsection}>
            <List
              title='Conditional & Chained Actions'
              items={['Conditional actions (contains / not contains, etc.)', 'Wait for certain actions to finish before continuing', 'Create actions from existing items (duplicate across modules)']}
            />
            <List
              className={styles.second}
              title='Queue Behavior'
              items={['Do Not Queue (run immediately)', 'Global queue', 'Per-command queue', 'Custom queue groups (prevent overlaps across related commands)']}
            />
          </TwoColumn>

          <p className={styles.reasoning}>Even advanced automations stay reliable and easy to reason about.</p>
        </Section>

        <Section id='reliability' title='Designed to Stay Running' icon={<FontAwesomeIcon icon={faShield as IconDefinition} className={styles.icon} />}>
          <p>Draco.Bot is built for long streams and real-world use. It runs locally, reconnects when needed, and includes safety nets so you can keep streaming.</p>

          <List
            title='Reliability Highlights'
            items={['Runs locally (no cloud delays)', 'Automatic reconnects for Twitch and OBS', 'Connect to OBS only when needed', 'Configuration backups and recovery']}
          />
        </Section>

        <Section id='love' title='Community & Extras' icon={<FontAwesomeIcon icon={faHeart as IconDefinition} className={styles.icon} />}>
          <h4>Reward Your Regulars</h4>
          <p>Give VIPs, subs, and trusted viewers special access, and keep your community feeling seen.</p>
          <TwoColumn className={styles.subsection}>
            <div className={styles.subsection}>
              <List items={['Restrict commands by role (VIP, sub, mod, etc.)', 'Allow / deny specific users', 'Automatic greetings for regulars']} />
            </div>
            <div className={styles.subsection}>
              <List items={['Raffles (with entry limits and event hooks)', 'Dashboard stream summary (viewers, subs, bits, etc.)', 'Star / unstar chat messages and respond to those events']} />
            </div>
          </TwoColumn>
        </Section>

        <Section icon={<FontAwesomeIcon icon={faDownload as IconDefinition} className={styles.icon} />}>
          <DownloadAndDiscordButtons />
        </Section>
      </div>
    </ChatbotWrapper>
  )
}

export default Features
